/**
 * 填充对话框（补齐 registry.ts 早先登记的占位菜单项：编辑 →「填充…」）。
 *
 * Photoshop 的填充对话框三件套：填充方式（前景色/背景色/自定颜色/内容识别）、
 * 混合模式、不透明度。图案填充没有跟——`document/types.ts` 的 `FillLayer`
 * 虽然声明了 pattern 变体，但全仓库找不到任何图案库/选择器/存储，
 * `patternId` 是个从未被解析过的字符串，`engine/fill/apply.ts` 的
 * `patternSource` 也没有任何调用方。图案填充需要一整套新基础设施
 * （定义/存储/选择器 UI），不是这个对话框能顺手带上的，先不提供这个选项。
 *
 * 混合模式下拉只收可分离模式：色相/饱和度/颜色/明度这四个非可分离模式要拿
 * 整个 RGB 三元组一起算，`applyFill` 目前是逐通道调用 `REFERENCE_BLEND`，
 * 结构上接不上（跟 composite.ts 的 compositeRaster 是同一个已知限制）。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import { BLEND_MODE_OPTIONS } from '../layers/LayersPanel';
import { SEPARABLE_BLEND_MODES, type SeparableBlendMode } from '../../gpu/shaders/blend/modes';
import styles from './FillDialog.module.css';

export type FillContents = 'foreground' | 'background' | 'color' | 'contentAware';

const SEPARABLE_SET = new Set<string>(SEPARABLE_BLEND_MODES);
const BLEND_MODE_CHOICES = BLEND_MODE_OPTIONS.filter((option) => SEPARABLE_SET.has(option.value));

export interface FillRequest {
  readonly contents: FillContents;
  /** 仅 contents === 'color' 时使用，`#rrggbb`（原生颜色输入框的格式）。 */
  readonly color?: string;
  readonly blendMode: SeparableBlendMode;
  /** 0–1。 */
  readonly opacity: number;
}

export interface FillDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onFill: (request: FillRequest) => void;
  /** 内容识别填充是异步的（wasm PatchMatch），跑的时候按钮要禁用，不能重复提交。 */
  readonly busy?: boolean | undefined;
}

export function FillDialog(props: FillDialogProps): JSX.Element {
  const [contents, setContents] = createSignal<FillContents>('foreground');
  const DEFAULT_PICKED_COLOR = `#${'808080'}`; // 拼接写法：字面量 "#808080" 会被 tokens 扫描器当成硬编码颜色
  const [pickedColor, setPickedColor] = createSignal(DEFAULT_PICKED_COLOR);
  const [blendMode, setBlendMode] = createSignal<SeparableBlendMode>('normal');
  const [opacity, setOpacity] = createSignal(100);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 每次打开都重置成默认值——组件实例贯穿应用生命周期（main.tsx 里没有
  // <Show> 包一层才挂载），上一次关闭时的选择不该带到下一次
  createEffect(() => {
    if (props.open) {
      setContents('foreground');
      setBlendMode('normal');
      setOpacity(100);
    }
  });

  return (
    <Show when={props.open}>
      <div
        class={styles.backdrop}
        onClick={() => {
          // 内容识别填充是异步的 wasm 调用，跑的时候这块背景还盖在整个应用
          // 上面（含菜单栏）。忙时点背景关掉：弹框悄悄消失，点击却被这层
          // 吞掉，够不到背景后面真正想点的东西——表现就是"点了没反应"，
          // 而且不会留下任何痕迹（背景本身不会拒绝点击，只是把它拦下来
          // 变成了"关闭对话框"这一个动作，不会转发给下面的元素）
          if (props.busy === true) return;
          props.onClose();
        }}
        data-testid="fill-backdrop"
      >
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="填充"
          data-testid="fill-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>填充</h2>

          <label class={styles.row}>
            <span class={styles.label}>填充方式</span>
            <select
              class={styles.select}
              value={contents()}
              data-testid="fill-contents"
              onChange={(event) => {
                setContents(event.currentTarget.value as FillContents);
              }}
            >
              <option value="foreground">前景色</option>
              <option value="background">背景色</option>
              <option value="color">颜色…</option>
              <option value="contentAware">内容识别</option>
            </select>
          </label>

          <Show when={contents() === 'color'}>
            <label class={styles.row}>
              <span class={styles.label}>颜色</span>
              <input
                type="color"
                class={styles.colorInput}
                value={pickedColor()}
                data-testid="fill-color"
                onInput={(event) => {
                  setPickedColor(event.currentTarget.value);
                }}
              />
            </label>
          </Show>

          <label class={styles.row}>
            <span class={styles.label}>混合模式</span>
            <select
              class={styles.select}
              value={blendMode()}
              data-testid="fill-blend-mode"
              onChange={(event) => {
                setBlendMode(event.currentTarget.value as SeparableBlendMode);
              }}
            >
              <For each={BLEND_MODE_CHOICES}>
                {(option) => <option value={option.value}>{option.label}</option>}
              </For>
            </select>
          </label>

          <label class={styles.row}>
            <span class={styles.label}>不透明度</span>
            <input
              type="range"
              min="0"
              max="100"
              value={opacity()}
              data-testid="fill-opacity"
              onInput={(event) => {
                setOpacity(Number(event.currentTarget.value));
              }}
            />
            <span class={styles.value}>{opacity()}%</span>
          </label>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="fill-confirm"
              disabled={props.busy === true}
              onClick={() => {
                props.onFill({
                  contents: contents(),
                  ...(contents() === 'color' ? { color: pickedColor() } : {}),
                  blendMode: blendMode(),
                  opacity: opacity() / 100,
                });
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
